import { create } from 'zustand';
import { workspacesApi, type Workspace } from '@/api/workspaces';
import { extractApiError } from '@/api/client';

/**
 * Phase 12.1 — current-workspace context (subdomain-aware).
 *
 * Lifecycle:
 *   - On app mount, WorkspaceProvider calls detect()
 *   - detect() reads window.location.hostname; if it matches `{slug}.{MAIN}`
 *     it fetches /api/workspaces/:slug. On main domain it just clears.
 *   - Backend errors map to:
 *       404 WORKSPACE_NOT_FOUND  → error.code='WORKSPACE_NOT_FOUND'
 *       410 WORKSPACE_ARCHIVED   → error.code='WORKSPACE_ARCHIVED', .archivedReason
 *
 * App.tsx checks `error` and renders a full-screen blocker before any
 * normal routes. `current` is read by ChannelSidebar / TopBar branding.
 *
 * NOT persisted — recomputed every page load (subdomain may have changed,
 * archive state could change underneath us).
 */

const MAIN_DOMAIN: string =
  (import.meta.env.VITE_MAIN_DOMAIN as string | undefined) ?? 'chat56.ru';

export interface WorkspaceError {
  code: 'WORKSPACE_NOT_FOUND' | 'WORKSPACE_ARCHIVED' | 'NETWORK_ERROR';
  message: string;
  slug: string;
  archivedReason?: string | null;
}

interface WorkspaceState {
  /** Loaded workspace context, or null on main domain / before detect(). */
  current: Workspace | null;
  /** Set on archived/missing subdomain. App.tsx renders a blocker. */
  error: WorkspaceError | null;
  loading: boolean;
  /** True after detect() finished at least once (success or error). */
  detected: boolean;

  detect: () => Promise<void>;
  clear: () => void;
}

/**
 * Mirror of backend's extractWorkspaceSlugFromHost. Returns the subdomain
 * slug if the current host is `{slug}.{MAIN_DOMAIN}` and the prefix doesn't
 * look like a reserved subdomain (`www`, `api`, etc — we let backend decide
 * authoritatively, but mirror minimal rejection here so we don't fire bogus
 * fetches for `www`).
 */
function extractSlugFromCurrentHost(): string | null {
  const host = window.location.hostname.toLowerCase();
  const main = MAIN_DOMAIN.toLowerCase();
  if (host === main) return null;
  const suffix = '.' + main;
  if (!host.endsWith(suffix)) return null;
  const prefix = host.slice(0, -suffix.length);
  if (!prefix || prefix.includes('.')) return null;
  // Minimal client-side reject for known reserved labels (saves a 404 round-trip).
  // Backend has the canonical list — if a slug slips through, backend returns 404.
  const reserved = new Set(['www', 'api', 'admin', 'mail', 'cdn', 'static', 'app', 'docs']);
  if (reserved.has(prefix)) return null;
  if (!/^[a-z0-9-]+$/.test(prefix)) return null;
  return prefix;
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  current: null,
  error: null,
  loading: false,
  detected: false,

  detect: async () => {
    const slug = extractSlugFromCurrentHost();
    if (!slug) {
      // Main domain — done.
      set({ current: null, error: null, loading: false, detected: true });
      return;
    }
    set({ loading: true });
    try {
      const ws = await workspacesApi.get(slug);
      if (ws.archived) {
        set({
          current: null,
          error: {
            code: 'WORKSPACE_ARCHIVED',
            message: 'Этот workspace был закрыт',
            slug,
            archivedReason: ws.archivedReason ?? null,
          },
          loading: false,
          detected: true,
        });
        return;
      }
      set({ current: ws, error: null, loading: false, detected: true });
    } catch (err) {
      const apiErr = extractApiError(err);
      // Distinguish 404 / 410 from random network errors.
      let code: WorkspaceError['code'] = 'NETWORK_ERROR';
      if (apiErr.code === 'WORKSPACE_NOT_FOUND' || apiErr.status === 404) code = 'WORKSPACE_NOT_FOUND';
      else if (apiErr.code === 'WORKSPACE_ARCHIVED' || apiErr.status === 410) code = 'WORKSPACE_ARCHIVED';
      set({
        current: null,
        error: { code, message: apiErr.message, slug },
        loading: false,
        detected: true,
      });
    }
  },

  clear: () => set({ current: null, error: null, loading: false, detected: true }),
}));

/** Convenience: where to redirect when a subdomain is dead. */
export function mainDomainHomeUrl(): string {
  const proto = window.location.protocol;
  return `${proto}//${MAIN_DOMAIN}`;
}
